import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SbAsins, SbCreative } from "@front/m19-api-client";
import { listToSbAsins, sbAsinsToList } from "@front/m19-models";
import { AccountSelectionService, BrandAssetsService } from "@front/m19-services";
import {
  IButtonDirective,
  ITooltipDirective,
  MODAL_DATA,
  ModalRef,
  IIconComponent,
  SpinnerComponent,
} from "@front/m19-ui";
import { TranslocoModule } from "@jsverse/transloco";
import { switchMap } from "rxjs";

interface AdLineAsin {
  asin: string;
  deleted: boolean;
}

interface AdLineResult {
  asins: AdLineAsin[];
  isDuplicate: boolean;
  isEmpty: boolean;
  impacted?: boolean;
}

interface CreativeAdLineResult {
  creativeId: number;
  adLines: Array<AdLineResult>;
  impacted?: boolean;
}

export interface CreativeSbAsinsResult {
  creative: SbCreative;
  asins: Array<SbAsins>;
}

@Component({
  selector: "app-tacos-sb-asins-check",
  imports: [IButtonDirective, ITooltipDirective, TranslocoModule, SpinnerComponent, IIconComponent],
  templateUrl: "./tacos-sb-asins-check.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TacosSbAsinsCheckComponent implements OnInit {
  private readonly accountSelection = inject(AccountSelectionService);
  private readonly brandAssetService = inject(BrandAssetsService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly data = inject<TacosSbAsinsCheckData>(MODAL_DATA);
  private readonly modalRef = inject(ModalRef<Array<CreativeSbAsinsResult>>);

  newSbAsins = output<Array<CreativeSbAsinsResult>>();

  adLinesResult = signal<Array<CreativeAdLineResult>>([]);
  atLeastOneImpacted = computed(() => this.adLinesResult().some((creative) => creative.impacted));

  hasAtLeastOneAdLine = computed(() =>
    this.adLinesResult().some((creative) => creative.adLines.some((adLine) => adLine.asins.some((a) => !a.deleted))),
  );

  noAdLineTooltip = computed(() => (this.hasAtLeastOneAdLine() ? "" : "You must have at least one ad line"));

  loading = signal(false);
  sbCreatives = signal<SbCreative[] | undefined>(undefined);

  ngOnInit() {
    this.loading.set(true);
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        switchMap((am) => this.brandAssetService.getSbCreativesPerStrategy(am.accountId, am.marketplace)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sbCreatives) => {
        const creatives = sbCreatives.get(this.data.sbStrategyId!);
        this.sbCreatives.set(creatives!);

        const creativeAdLineResults: Array<CreativeAdLineResult> = [];
        for (const creative of creatives!) {
          const adLines = this.processRemoval(creative);
          const impacted = adLines.some((adLine) => adLine.impacted);
          creativeAdLineResults.push({
            creativeId: creative.creativeId,
            adLines: adLines,
            impacted: impacted,
          });
        }
        this.adLinesResult.set(creativeAdLineResults);
        this.loading.set(false);
        if (!this.atLeastOneImpacted()) {
          this.confirmDeletion();
          this.modalRef.close();
        }
      });
  }

  private processRemoval(creative: SbCreative): Array<AdLineResult> {
    const asinsToRemove = this.data.asinsToRemove;
    const sbAsins = creative.creativeAsins!;

    const res: Array<AdLineResult> = [];

    for (const group of sbAsins) {
      const adLineResult: AdLineResult = {
        asins: sbAsinsToList(group).map((asin) => ({
          asin,
          deleted: asinsToRemove.includes(asin),
        })),
        isDuplicate: false,
        isEmpty: false,
      };

      const adLineResultAsinToArray = this.keptAsins(adLineResult);
      adLineResult.isDuplicate = res.some((adLine) => {
        if (adLine.isEmpty) return false;
        const asinToArray = this.keptAsins(adLine);
        return (
          asinToArray.length === adLineResultAsinToArray.length &&
          asinToArray.every((asin, index) => asin === adLineResultAsinToArray[index])
        );
      });

      adLineResult.isEmpty = adLineResult.asins.every((a) => a.deleted);
      adLineResult.impacted =
        !adLineResult.isDuplicate && !adLineResult.isEmpty && adLineResult.asins.some((a) => a.deleted);

      res.push(adLineResult);
    }

    return res;
  }

  private keptAsins(adLine: AdLineResult): string[] {
    return adLine.asins.filter((a) => !a.deleted).map((a) => a.asin);
  }

  confirmDeletion() {
    const creativeSbAsinsResults: Array<CreativeSbAsinsResult> = [];
    const filtered = this.adLinesResult().map((creative) => {
      return {
        creative: this.sbCreatives()!.find((c) => c.creativeId === creative.creativeId)!,
        adLines: creative.adLines.filter((adLine) => !adLine.isEmpty && !adLine.isDuplicate),
      };
    });

    for (const creative of filtered) {
      creativeSbAsinsResults.push({
        creative: creative.creative,
        asins: creative.adLines.map((adLine) => listToSbAsins(this.keptAsins(adLine))),
      });
    }
    this.modalRef.close(creativeSbAsinsResults);
  }

  onCancel() {
    this.modalRef.close();
  }
}

export interface TacosSbAsinsCheckData {
  sbStrategyId: number;
  asinsToRemove: Array<string>;
}
